import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import MarketingShell from "./MarketingShell";
import { ArrowLeft, Warning, CheckCircle } from "@phosphor-icons/react";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

const STATUS_COLORS = {
  completed: { bg: "#F0FDF4", fg: "#16A34A", label: "Completed" },
  failed: { bg: "#FEF2F2", fg: "#DC2626", label: "Failed" },
  mapping: { bg: "#EFF6FF", fg: "#0055FF", label: "Mapping" },
  validated: { bg: "#EFF6FF", fg: "#0055FF", label: "Validated" },
  importing: { bg: "#FEF3C7", fg: "#92400E", label: "Importing" },
  uploading: { bg: "#F5F5F5", fg: "#525252", label: "Uploading" },
};

export default function ImportDetailPage() {
  const { batchId } = useParams();
  const nav = useNavigate();
  const [batch, setBatch] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [errors, setErrors] = useState([]);
  const [tab, setTab] = useState("contacts");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [b, c, e] = await Promise.all([
          api.get(`/marketing/imports/${batchId}`),
          api.get(`/marketing/imports/${batchId}/contacts`),
          api.get(`/marketing/imports/${batchId}/errors`).catch(() => ({ data: [] })),
        ]);
        setBatch(b.data);
        setContacts(c.data || []);
        setErrors(e.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load import");
      }
      setLoading(false);
    })();
  }, [batchId]);

  if (loading) return <MarketingShell><p className="text-sm text-[#A3A3A3]">Loading…</p></MarketingShell>;
  if (!batch) return <MarketingShell><p className="text-sm text-[#A3A3A3]">Import not found.</p></MarketingShell>;

  const status = STATUS_COLORS[batch.status] || STATUS_COLORS.uploading;
  const mapping = batch.column_map || {};

  return (
    <MarketingShell>
      <button onClick={() => nav("/marketing/imports")}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-[#0055FF] mb-4"
              data-testid="back-to-imports">
        <ArrowLeft size={14} /> Back to Imports
      </button>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{batch.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5" style={{ backgroundColor: status.bg, color: status.fg }}>
              {status.label}
            </span>
            {batch.vendor && <span className="text-xs text-[#525252]">· {batch.vendor}</span>}
            <span className="text-xs text-[#A3A3A3]">· uploaded {formatLocal(batch.uploaded_at)}</span>
            {batch.original_filename && <span className="text-xs text-[#A3A3A3] font-mono">· {batch.original_filename}</span>}
          </div>
        </div>
      </div>

      {/* Metadata stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          ["Total rows", batch.row_count, "#0A0A0A"],
          ["Imported", batch.imported_count, "#16A34A"],
          ["Updated", batch.updated_count, "#0055FF"],
          ["Skipped", batch.skipped_count, "#D97706"],
          ["Errors", batch.error_count, "#DC2626"],
        ].map(([label, val, color]) => (
          <div key={label} className="border border-[#E5E5E5] bg-white p-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">{label}</div>
            <div className="text-2xl font-black tabular-nums mt-0.5" style={{ color, fontFamily: "'Cabinet Grotesk',sans-serif" }}>{val ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Provenance + column mapping */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div className="border border-[#E5E5E5] bg-white p-4">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-2">Provenance & Consent</h3>
          <dl className="text-xs space-y-1.5">
            <div className="flex"><dt className="w-40 text-[#A3A3A3]">Vendor / source</dt><dd className="text-[#0A0A0A] font-semibold">{batch.vendor || "—"}</dd></div>
            <div className="flex"><dt className="w-40 text-[#A3A3A3]">Consent declaration</dt><dd className="text-[#0A0A0A]">{batch.consent_declaration || "—"}</dd></div>
            <div className="flex"><dt className="w-40 text-[#A3A3A3]">Acquisition date</dt><dd className="text-[#0A0A0A]">{batch.acquisition_date ? formatLocalDate(batch.acquisition_date) : "—"}</dd></div>
            <div className="flex"><dt className="w-40 text-[#A3A3A3]">Provenance note</dt><dd className="text-[#525252] flex-1">{batch.provenance_note || "—"}</dd></div>
            <div className="flex"><dt className="w-40 text-[#A3A3A3]">Uploaded by</dt><dd className="text-[#0A0A0A] font-mono text-[11px]">{batch.uploaded_by || "—"}</dd></div>
          </dl>
        </div>
        <div className="border border-[#E5E5E5] bg-white p-4">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-2">Column Mapping</h3>
          {Object.keys(mapping).length === 0 ? (
            <p className="text-xs text-[#A3A3A3]">No column map stored.</p>
          ) : (
            <dl className="text-xs grid grid-cols-2 gap-y-1.5">
              {Object.entries(mapping).map(([src, dst]) => (
                <div key={src} className="flex items-center gap-1.5 col-span-2">
                  <code className="bg-[#FAFAFA] px-1.5 py-0.5 text-[11px] text-[#525252] border border-[#E5E5E5]">{src}</code>
                  <span className="text-[#A3A3A3]">→</span>
                  <code className="bg-[#EFF6FF] px-1.5 py-0.5 text-[11px] text-[#0055FF] font-bold">{dst || "(ignored)"}</code>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>

      {/* Tabs: Contacts / Errors */}
      <div className="flex border-b border-[#E5E5E5] mb-4">
        <button onClick={() => setTab("contacts")}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 ${tab === "contacts" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#525252]"}`}
                data-testid="tab-contacts">
          <CheckCircle size={12} /> Contacts ({contacts.length})
        </button>
        <button onClick={() => setTab("errors")}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 ${tab === "errors" ? "border-[#DC2626] text-[#DC2626]" : "border-transparent text-[#525252]"}`}
                data-testid="tab-errors">
          <Warning size={12} /> Errors ({errors.length})
        </button>
      </div>

      {tab === "contacts" && (
        <div className="bg-white border border-[#E5E5E5]">
          {contacts.length === 0 ? (
            <p className="p-8 text-center text-sm text-[#A3A3A3]">
              No contacts were imported from this batch.
              {batch.status !== "completed" && <> The import is in <strong>{batch.status}</strong> state.</>}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                <tr>
                  <th className="text-left px-5 py-2 font-bold">Email</th>
                  <th className="text-left px-3 py-2 font-bold">Name</th>
                  <th className="text-left px-3 py-2 font-bold">Company</th>
                  <th className="text-left px-3 py-2 font-bold">Job Title</th>
                  <th className="text-left px-3 py-2 font-bold">Phone</th>
                  <th className="text-left px-3 py-2 font-bold">Country</th>
                  <th className="text-left px-3 py-2 font-bold">Consent</th>
                  <th className="text-right px-5 py-2 font-bold">Imported</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr key={c.id} className={`border-t border-[#F5F5F5] ${c.is_suppressed ? "bg-[#FFFBEB]/40" : ""}`} data-testid={`import-contact-${c.id}`}>
                    <td className="px-5 py-2.5 font-semibold text-[#0A0A0A]">{c.email}</td>
                    <td className="px-3 py-2.5 text-[#525252]">{c.full_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</td>
                    <td className="px-3 py-2.5 text-[#525252] max-w-xs truncate">{c.company_name || "—"}</td>
                    <td className="px-3 py-2.5 text-[#525252]">{c.job_title || "—"}</td>
                    <td className="px-3 py-2.5 text-[#525252] tabular-nums">{c.phone || "—"}</td>
                    <td className="px-3 py-2.5 text-[#525252]">{c.country || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-[#525252] uppercase tracking-wider">{c.consent_status || "unknown"}</td>
                    <td className="px-5 py-2.5 text-right text-xs text-[#A3A3A3] tabular-nums">
                      {c.linked_at ? formatLocalDate(c.linked_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "errors" && (
        <div className="bg-white border border-[#E5E5E5]">
          {errors.length === 0 ? (
            <p className="p-8 text-center text-sm text-[#A3A3A3]">No errors recorded for this batch.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                <tr>
                  <th className="text-left px-5 py-2 font-bold w-16">Row</th>
                  <th className="text-left px-3 py-2 font-bold">Error</th>
                  <th className="text-left px-5 py-2 font-bold">Raw data</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((e, i) => (
                  <tr key={i} className="border-t border-[#F5F5F5] align-top" data-testid={`import-error-${i}`}>
                    <td className="px-5 py-2.5 text-[#525252] tabular-nums">{e.row_number}</td>
                    <td className="px-3 py-2.5">
                      <div className="text-xs font-bold text-[#DC2626] uppercase tracking-wider">{e.error_code}</div>
                      <div className="text-xs text-[#525252] mt-0.5">{e.error_message}</div>
                    </td>
                    <td className="px-5 py-2.5 text-[11px] text-[#525252] font-mono break-all">
                      {e.raw_row ? JSON.stringify(e.raw_row) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </MarketingShell>
  );
}
